// src/components/CampanaDashboard.js
import React, { useEffect, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
    Cell,
} from 'recharts';
import { Typography, Box, Paper, CircularProgress } from '@mui/material';
import { sendRequest } from '../../../../services/request';
import CustomXAxisTick from './CustomXAxisTick';
import CustomTooltipResumen from './CustomTooltipResumen'; // Importar el tooltip personalizado

const EquiposDashboard = ({ pais }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await sendRequest(
                    'get',
                    `${URL}/${pais}/equipos/dashboard`,
                );
                setData(res.data || []);
            } catch (error) {
                console.error('Error cargando los datos:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [URL, pais]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'center',
                alignItems: 'stretch',
                gap: 4,
                padding: 4,
                flexWrap: 'wrap',
            }}
        >
            {/* TARJETA 1 */}
            <Paper elevation={3} sx={{ flex: 1, minWidth: 0, padding: 4 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    textAlign="center"
                    color="primary"
                >
                    Resumen de Equipos
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                        data={data}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                        barCategoryGap={10}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="nombre"
                            tick={<CustomXAxisTick />}
                            interval={0}
                            height={80}
                        />
                        <YAxis />
                        <Tooltip
                            content={<CustomTooltipResumen />}
                            wrapperStyle={{ zIndex: 1300 }}
                        />
                        <Legend verticalAlign="top" />
                        <Bar
                            dataKey="clientesAsignados"
                            name="Clientes Asignados"
                            fill="#0D47A1"
                            barSize={20}
                        >
                            <LabelList
                                dataKey="clientesAsignados"
                                position="insideTop"
                                fill="#fff"
                                fontSize={12}
                            />
                        </Bar>
                        <Bar
                            dataKey="preventas"
                            name="Envíos Totales"
                            fill="#1976D2"
                            barSize={20}
                        >
                            <LabelList
                                dataKey="preventas"
                                position="insideTop"
                                fill="#fff"
                                fontSize={12}
                            />
                        </Bar>
                        <Bar
                            dataKey="ventasEfectivas"
                            name="Ventas Efectivas"
                            fill="#64B5F6"
                            barSize={20}
                        >
                            <LabelList
                                dataKey="ventasEfectivas"
                                position="insideTop"
                                fill="#fff"
                                fontSize={12}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Paper>

            {/* TARJETA 2 */}
            <Paper elevation={3} sx={{ flex: 1, minWidth: 0, padding: 4 }}>
                <Typography
                    variant="h6"
                    gutterBottom
                    textAlign="center"
                    color="primary"
                >
                    Porcentaje de Efectividad
                </Typography>
                <ResponsiveContainer
                    width="100%"
                    height={Math.max(300, data.length * 50)}
                >
                    <BarChart
                        layout="vertical"
                        data={data}
                        margin={{ top: 20, right: 60, left: 20, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tickFormatter={(val) => `${val}%`}
                        />
                        <YAxis
                            dataKey="nombre"
                            type="category"
                            width={150}
                            tick={({ x, y, payload }) => {
                                const text = payload.value;
                                const maxLength = 25;
                                const display =
                                    text.length > maxLength
                                        ? `${text.slice(0, maxLength)}…`
                                        : text;

                                return (
                                    <g transform={`translate(${x},${y})`}>
                                        <title>{text}</title>
                                        <text
                                            x={0}
                                            y={0}
                                            dx={-10}
                                            dy={5}
                                            textAnchor="end"
                                            fill="#333"
                                            fontSize={11}
                                        >
                                            {display}
                                        </text>
                                    </g>
                                );
                            }}
                        />
                        <Tooltip
                            content={<CustomTooltipResumen />}
                            wrapperStyle={{ zIndex: 1300 }}
                        />
                        <Bar
                            dataKey="porcentajeEfectividad"
                            name="Efectividad"
                            isAnimationActive={false}
                            radius={[0, 5, 5, 0]}
                        >
                            {data.map((entry, index) => {
                                const value = entry.porcentajeEfectividad;
                                if (!value || value === 0) {
                                    return (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill="transparent"
                                        />
                                    );
                                }
                                const color =
                                    value >= 70
                                        ? '#00C853'
                                        : value >= 40
                                        ? '#F9A825'
                                        : '#D32F2F';
                                return (
                                    <Cell key={`cell-${index}`} fill={color} />
                                );
                            })}
                            <LabelList
                                dataKey="porcentajeEfectividad"
                                position="insideRight"
                                formatter={(val) =>
                                    val === 0
                                        ? ''
                                        : `${parseFloat(val).toFixed(2)}%`
                                }
                                fill="#fff"
                                fontSize={12}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Paper>
        </Box>
    );
};

export default EquiposDashboard;
